import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Dialog,
  DialogContent,
  TextField,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  InputAdornment,
  Box,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  Assignment as EngagementIcon,
  ReportProblem as FindingIcon,
  TrackChanges as MeasureIcon,
  Description as ReportIcon,
  AccountTree as AuditObjectIcon,
  AutoAwesome as AiIcon,
} from '@mui/icons-material';
import { useDebounce } from '@/hooks/useDebounce';
import { useKiFunktion } from '@/hooks/useKiFunktion';
import { AnalysisTable } from '@/components/ai/AnalysisTable';
import { KiKnopf } from '@/components/ai/KiKnopf';
import { Satzverweise, VerweisZiel } from '@/components/ai/Satzverweise';
import { seiteFuer, useAskRecordsMutation, useLazyGlobalSearchQuery } from '@/store/api/searchApi';
import { useAppDispatch, useAppSelector } from '@/store';
import { setGlobalSearchOpen } from '@/store/slices/uiSlice';
import { fehlermeldung } from '@/utils/form-payload';
import { standText } from '@/utils/stand';

const entityIcons: Record<string, React.ReactNode> = {
  engagement: <EngagementIcon />,
  finding: <FindingIcon />,
  measure: <MeasureIcon />,
  report: <ReportIcon />,
  // Der Dienst nennt den Typ so, wie ihn der Rest der Anwendung nennt; hier
  // stand vorher ein Name, den er nie schickt, also blieb das Zeichen leer.
  audit_universe_item: <AuditObjectIcon />,
  document: <ReportIcon />,
};

/** Wie der Typ auf dem Ergebnis steht. */
const entityLabels: Record<string, string> = {
  engagement: 'Prüfungsauftrag',
  finding: 'Feststellung',
  measure: 'Maßnahme',
  report: 'Bericht',
  audit_universe_item: 'Prüfungsobjekt',
  document: 'Dokument',
};

type Modus = 'suchen' | 'fragen';

export const SearchBar: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const open = useAppSelector((state) => state.ui.globalSearchOpen);
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounce(query, 300);
  const [search, { data, isLoading, isError }] = useLazyGlobalSearchQuery();
  // Was gezeigt wird, gehört zur Frage im Feld. Leert jemand das Feld, ist
  // die Antwort auf die vorige Frage keine Antwort mehr — sie stünde sonst
  // da, bis die nächste kommt.
  const ergebnis = query.trim().length >= 2 ? data : undefined;

  // Zweite Betriebsart (K12): eine Frage statt eines Stichworts. Nur, wenn
  // der Mandant die Funktion freigeschaltet hat und die Person sie darf —
  // sonst gibt es den Umschalter nicht, und die Suche bleibt, wie sie ist.
  const ki = useKiFunktion('record_question');
  const [modus, setModus] = useState<Modus>('suchen');
  const [fragen, { data: antwort, isLoading: fragt, reset: antwortVergessen }] = useAskRecordsMutation();
  const [frageFehler, setFrageFehler] = useState('');
  // Dieselbe Tür gibt zwei Arten von Antwort zurück. Die gerechnete bekommt
  // ihre eigene Darstellung; die formulierte bleibt, wie sie war.
  const auswertung = antwort?.mode === 'analysis' ? antwort : undefined;
  const bestand = antwort && antwort.mode !== 'analysis' ? antwort : undefined;
  const verweise = useMemo(
    () =>
      new Map<string, VerweisZiel>(
        (bestand?.records ?? []).map((r) => [
          r.key,
          { pfad: seiteFuer(r.type, r.id), titel: `${entityLabels[r.type] ?? r.type}: ${r.title} — ${standText(r.status)}` },
        ]),
      ),
    [antwort],
  );

  useEffect(() => {
    if (modus === 'suchen' && debouncedQuery.length >= 2) {
      search({ q: debouncedQuery, limit: 10 });
    }
  }, [debouncedQuery, modus, search]);

  const handleClose = () => {
    dispatch(setGlobalSearchOpen(false));
    setQuery('');
    setFrageFehler('');
    antwortVergessen();
  };

  const handleSelect = (url: string | null) => {
    if (!url) return;
    navigate(url);
    handleClose();
  };

  const stelleFrage = async () => {
    const frage = query.trim();
    if (frage.length < 3 || fragt) return;
    setFrageFehler('');
    try {
      await fragen({ question: frage }).unwrap();
    } catch (e) {
      setFrageFehler(fehlermeldung(e, 'Die Frage konnte nicht beantwortet werden'));
    }
  };

  // Keyboard shortcut
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        dispatch(setGlobalSearchOpen(true));
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [dispatch]);

  const fussnote = bestand
    ? bestand.mode === 'count'
      ? 'Gezählt hat die Suche, nicht das Modell.'
      : `Aus ${bestand.hitsConsidered} von ${bestand.hitsTotal} Treffern — ${bestand.semantic ? 'nach Wort und Bedeutung' : 'nur nach Wörtern'}${bestand.model ? `, formuliert von ${bestand.model}` : ''}. Nicht gespeichert; maßgeblich ist die Liste, nicht der Absatz.`
    : '';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      // Breit genug für eine Antwort mit Verweisen und die Trefferliste
      // darunter (K12); für eine Trefferzeile war „sm" gerade recht, für einen
      // Absatz mit Marken darin nicht mehr.
      maxWidth="md"
      PaperProps={{ sx: { position: 'fixed', top: '10%', borderRadius: 2, maxHeight: '80vh' } }}
    >
      <DialogContent sx={{ p: 0 }}>
        {/* Zwei Zeilen Platz und etwas Luft: wer nach Bedeutung sucht, schreibt
            einen Satz, kein Stichwort — der soll ganz zu sehen sein. Enter bleibt
            Enter und macht keine dritte Zeile; in der Betriebsart „Frage stellen“
            schickt es die Frage ab. */}
        <TextField
          fullWidth
          autoFocus
          multiline
          maxRows={2}
          placeholder={modus === 'fragen' ? 'Frage an den Bestand … (Enter fragt)' : `${t('actions.search')}... (Ctrl+K)`}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (modus === 'fragen' && !e.shiftKey) void stelleFrage();
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                {modus === 'fragen' ? <AiIcon sx={{ color: 'ki.main' }} /> : <SearchIcon color="action" />}
              </InputAdornment>
            ),
            endAdornment: isLoading || fragt ? (
              <InputAdornment position="end">
                <CircularProgress size={20} />
              </InputAdornment>
            ) : null,
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 0,
              px: 2.5,
              py: 1.75,
              fontSize: '1.1rem',
              '& fieldset': { border: 'none' },
            },
          }}
        />
        {ki.verfuegbar && (
          <Box sx={{ px: 2.5, pb: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={modus}
              onChange={(_e, wert: Modus | null) => {
                if (wert) {
                  setModus(wert);
                  setFrageFehler('');
                }
              }}
              aria-label="Betriebsart"
            >
              <ToggleButton value="suchen">Suchen</ToggleButton>
              <ToggleButton value="fragen" sx={{ '&.Mui-selected': { color: 'ki.main', borderColor: 'ki.main' } }}>
                <AiIcon fontSize="small" sx={{ mr: 0.5 }} />
                Frage stellen
              </ToggleButton>
            </ToggleButtonGroup>
            {modus === 'fragen' && (
              <>
                <Typography variant="caption" color="text.secondary" sx={{ flex: 1, minWidth: 180 }}>
                  Ein Satz, kein Stichwort. Die Antwort kommt nur aus Treffern, die Sie lesen dürfen — jede Aussage mit Verweis.
                </Typography>
                <KiKnopf
                  size="small"
                  laeuft={fragt}
                  disabled={query.trim().length < 3}
                  onClick={() => void stelleFrage()}
                  hinweis="Sucht nach Wort und Bedeutung in dem, was Sie lesen dürfen, und lässt das Modell aus den Treffern antworten — jede Aussage mit Verweis. Zählfragen beantwortet die Suche selbst. Nichts wird gespeichert."
                >
                  Fragen
                </KiKnopf>
              </>
            )}
          </Box>
        )}

        {modus === 'fragen' && frageFehler && (
          <Alert severity="error" sx={{ mx: 2.5, mb: 2 }}>{frageFehler}</Alert>
        )}
        {/*
          Die zweite Art von Antwort: gerechnet statt formuliert. Fragen nach
          dem, was fehlt, kann eine Suche nicht beantworten — Abwesenheit
          erzeugt keine Treffer.
        */}
        {modus === 'fragen' && auswertung && (
          <Box sx={{ px: 2.5, pb: 2, maxHeight: 520, overflow: 'auto' }}>
            <AnalysisTable a={auswertung} onSprung={handleSelect} />
          </Box>
        )}

        {modus === 'fragen' && bestand && (
          <Box sx={{ px: 2.5, pb: 2, maxHeight: 520, overflow: 'auto' }}>
            <Box sx={{ p: 2, borderRadius: 1, bgcolor: 'action.hover' }}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Frage: {bestand.question}
              </Typography>
              {bestand.answer.split(/\n\s*\n/).map((absatz, i) => (
                <Typography key={i} variant="body2" component="div" sx={{ mb: 1, '&:last-of-type': { mb: 0 } }}>
                  <Satzverweise text={absatz} verweise={verweise} onWahl={handleSelect} />
                </Typography>
              ))}
              {bestand.mode === 'count' && bestand.counts && (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.5 }}>
                  {Object.entries(bestand.counts).map(([art, anzahl]) => (
                    <Chip key={art} size="small" label={`${entityLabels[art] ?? art}: ${anzahl}`} />
                  ))}
                </Box>
              )}
              {bestand.countClaims.length > 0 && (
                <Alert severity="warning" sx={{ mt: 1.5 }}>
                  Der Text nennt eine Menge: {bestand.countClaims.join(', ')} — gezählt hat das Modell, und das zählt nicht zuverlässig. Maßgeblich ist die Liste der Treffer.
                </Alert>
              )}
              {bestand.unknownRefs.length > 0 && (
                <Alert severity="warning" sx={{ mt: 1 }}>Verweise ohne Treffer: {bestand.unknownRefs.join(', ')}.</Alert>
              )}
              {bestand.flaggedNumbers.length > 0 && (
                <Alert severity="warning" sx={{ mt: 1 }}>Zahlen, die in keinem Treffer stehen: {bestand.flaggedNumbers.join(', ')}.</Alert>
              )}
              {bestand.truncated && <Alert severity="info" sx={{ mt: 1 }}>Die Antwort wurde abgeschnitten.</Alert>}
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.5 }}>{fussnote}</Typography>
            </Box>
            {bestand.records.length > 0 && (
              <>
                <Typography variant="subtitle2" sx={{ mt: 1.5, mb: 0.5 }}>
                  Die Treffer ({bestand.records.length}{bestand.hitsTotal > bestand.records.length ? ` von ${bestand.hitsTotal}` : ''})
                </Typography>
                <List dense disablePadding>
                  {bestand.records.map((r) => {
                    const url = seiteFuer(r.type, r.id);
                    return (
                      <ListItemButton key={r.key} onClick={() => handleSelect(url)} disabled={!url} dense sx={{ borderRadius: 1 }}>
                        <Chip size="small" label={r.key} sx={{ mr: 1.5, minWidth: 40, height: 20 }} />
                        <ListItemText
                          primary={r.title}
                          secondary={`${entityLabels[r.type] ?? r.type} — ${standText(r.status)}`}
                          primaryTypographyProps={{ fontSize: '0.875rem' }}
                          secondaryTypographyProps={{ fontSize: '0.75rem' }}
                        />
                      </ListItemButton>
                    );
                  })}
                </List>
              </>
            )}
          </Box>
        )}

        {/* Wort und Bedeutung zugleich (K25): der Treffer, der nur dem Sinn nach
            passt, steht dann neben dem, der das Wort trägt — und die Suche sagt es. */}
        {modus === 'suchen' && !isError && ergebnis?.semantic && (
          <Box sx={{ px: 2, py: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip label="Suche nach Bedeutung" size="small" color="primary" variant="outlined" />
            <Typography variant="caption" color="text.secondary">
              Treffer nach Wort und Sinn
            </Typography>
          </Box>
        )}
        {modus === 'suchen' && isError && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="error">
              Die Suche ist gerade nicht erreichbar. Das Ergebnis wäre leer, aber nicht
              vollständig — bitte später erneut versuchen.
            </Typography>
          </Box>
        )}
        {modus === 'suchen' && !isError && ergebnis && ergebnis.results.length > 0 && (
          <List sx={{ maxHeight: 400, overflow: 'auto', pt: 0 }}>
            {ergebnis.results.map((result) => (
              <ListItemButton
                key={result.id}
                onClick={() => handleSelect(result.url)}
                // Ohne Seite kein Klick, der ins Leere führt — der Treffer
                // bleibt trotzdem stehen.
                disabled={!result.url}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  {entityIcons[result.entityType] ?? <SearchIcon />}
                </ListItemIcon>
                <ListItemText
                  primary={result.title}
                  secondary={result.description}
                  primaryTypographyProps={{ fontSize: '0.875rem' }}
                  secondaryTypographyProps={{ fontSize: '0.75rem', noWrap: true }}
                />
                <Chip
                  label={entityLabels[result.entityType] ?? result.entityType}
                  size="small"
                  variant="outlined"
                />
                {!result.url && (
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    keine Ansicht
                  </Typography>
                )}
              </ListItemButton>
            ))}
          </List>
        )}
        {modus === 'suchen' && !isError && ergebnis && ergebnis.results.length === 0 && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {t('pagination.noResults')}
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};
